import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { Image } from 'expo-image';
import { useRouter } from 'expo-router';
import { twJoin } from 'tailwind-merge';

import { Heading } from '@/components/ui/heading';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { useAbility } from '@/hooks/useAbility';
import { usePrimumAssets } from '@/hooks/usePrimumAssets';
import { type CancerTypeOption, type ContentLibraryItem } from '@/types/contentLibraryItems';
import { segmentClient } from '@/util/analytics';
import { useSizedImageUrl } from '@/util/imageWidths';
import { getHighlightedText } from '@/utils/contentLibraryHelpers';

export function ContentLibraryItemCard({
  contentLibraryItem,
  search,
}: {
  contentLibraryItem: ContentLibraryItem;
  search: string;
}) {
  const { getCloudFrontUrl } = usePrimumAssets();

  /** The grid is three columns on desktop and one on a phone. */
  const thumbnailSource = useSizedImageUrl(getCloudFrontUrl(contentLibraryItem.thumbnailUrl), 480);

  const { navigate } = useRouter();

  const canReadContentLibrary = useAbility().can('read', 'ContentLibraryItem');

  const displayedType = contentLibraryItem.typeOverride || contentLibraryItem.type || '';

  return (
    <Pressable
      className="hover:shadow-gray-200 overflow-hidden rounded-[4px] border border-solid border-primary-300 hover:bg-background-50 hover:shadow"
      key={contentLibraryItem.id}
      onPress={() => {
        segmentClient.track(ANALYTICS_EVENTS.CONTENT_LIBRARY_ITEM_CLICKED, {
          isUnauthenticated: !canReadContentLibrary,
          itemId: contentLibraryItem.id,
        });

        navigate(`/content-library/${contentLibraryItem.id}`);
      }}>
      <View className="aspect-[16/9] border-b border-solid border-primary-300">
        <Image
          alt={`${contentLibraryItem.title} thumbnail image`}
          contentFit="cover"
          source={thumbnailSource}
          style={{
            height: '100%',
            width: '100%',
          }}
        />
      </View>
      <View className="p-5">
        <View className="flex flex-row flex-wrap">
          <Text className="rounded-xs mb-2 mr-2 bg-background-info px-2 py-1 text-xs uppercase color-info-800">
            {getHighlightedText(displayedType, search, 'text-xs', 'leading-none')}
          </Text>
          {contentLibraryItem?.cancerTypes &&
            contentLibraryItem.cancerTypes.map((cancerType: CancerTypeOption) => {
              return (
                <Text
                  className="rounded-xs mb-2 mr-2 bg-background-muted px-2 py-1 text-xs uppercase color-background-800"
                  key={cancerType.id}>
                  {getHighlightedText(cancerType.name, search, 'text-xs', 'leading-none')}
                </Text>
              );
            })}
        </View>
        {isWeb ? (
          <>
            <Heading className={twJoin('mt-1 text-xl', search ? '' : 'clamp-to-three-lines-max')}>
              {getHighlightedText(contentLibraryItem.title, search, 'font-bold text-xl')}
            </Heading>
            <Text className={twJoin('mt-1', search ? '' : 'clamp-to-two-lines-max')}>
              {getHighlightedText(contentLibraryItem.shortDescription, search)}
            </Text>
          </>
        ) : (
          <>
            <Heading
              className="mt-1 text-xl"
              ellipsizeMode={search ? undefined : 'tail'}
              numberOfLines={search ? undefined : 3}>
              {getHighlightedText(contentLibraryItem.title, search, 'font-bold text-xl')}
            </Heading>
            <Text
              className="mt-1"
              ellipsizeMode={search ? undefined : 'tail'}
              numberOfLines={search ? undefined : 2}>
              {getHighlightedText(contentLibraryItem.shortDescription, search)}
            </Text>
          </>
        )}
        <Text className="mt-3 text-sm">
          {contentLibraryItem.watchTime ?? contentLibraryItem.readTime}
        </Text>
      </View>
    </Pressable>
  );
}
